/**
 * Feature-gate middleware – restricts access to routes that opt in via `definePageMeta`.
 * Uses `useTestingStore()` (populated from Optimizely in `05.optimizely.js`). When the configured
 * feature checks pass (`featureGateMode` `all` or `any`), navigation continues; otherwise responds
 * with 404.
 *
 * Nuxt awaits async plugins before route middleware runs, and with SSR the testing store is
 * populated during the Optimizely plugin before gated routes are evaluated — no extra hydration
 * wait is used here.
 *
 * If `featureGate` is missing, empty, or invalid, access is denied (404).
 *
 * Usage (add only on pages that should be gated; other routes are unaffected):
 *
 * ```vue
 * <script setup>
 * // Single feature — access only when this Optimizely flag is enabled
 * definePageMeta({
 *   middleware: 'feature-gate',
 *   featureGate: 'web_myFeatureKey',
 * });
 * </script>
 * ```
 *
 * ```vue
 * <script setup>
 * // Feature plus boolean variable — flag must be on and the variable must be `true`
 * definePageMeta({
 *   middleware: 'feature-gate',
 *   featureGate: { key: 'web_enableOGevents', var: 'country_enabled' },
 * });
 * </script>
 * ```
 *
 * ```vue
 * <script setup>
 * // Multiple features — all must be enabled (default)
 * definePageMeta({
 *   middleware: 'feature-gate',
 *   featureGate: ['web_featureA', 'web_featureB'],
 * });
 * </script>
 * ```
 *
 * ```vue
 * <script setup>
 * // Multiple feature + variable pairs — all must pass (default)
 * definePageMeta({
 *   middleware: 'feature-gate',
 *   featureGate: [
 *     { key: 'web_featureA', var: 'country_enabled' },
 *     { key: 'web_featureB', var: 'city_enabled' },
 *   ],
 * });
 * </script>
 * ```
 *
 * ```vue
 * <script setup>
 * // Multiple features — at least one must be enabled
 * definePageMeta({
 *   middleware: 'feature-gate',
 *   featureGate: ['web_featureA', 'web_featureB'],
 *   featureGateMode: 'any',
 * });
 * </script>
 * ```
 *
 * ```vue
 * <script setup>
 * // With other named middleware (order is left to right)
 * definePageMeta({
 *   middleware: ['feature-gate', 'validate-location'],
 *   featureGate: 'web_enableOGevents',
 * });
 * </script>
 * ```
 *
 * Meta options:
 * - `featureGate` (string | string[] | { key, var } | { key, var }[], required for access):
 *   Optimizely / testing store keys. Object form requires the flag enabled and `var` strictly
 *   `true` (missing / false denies access). Omit `var` to check the flag only.
 * - `featureGateMode` ('all' | 'any', optional): When `featureGate` is an array, require every
 *   entry (`all`, default) or any entry (`any`). Ignored for a single string or object.
 */
function getFeatureGateVariable(raw) {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed || null;
}

function parseFeatureGateEntry(item) {
  if (typeof item === 'string') {
    const key = item.trim();
    if (!key) {
      return null;
    }
    return {
      key,
      variable: null,
    };
  }

  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return null;
  }

  const key = typeof item.key === 'string' ? item.key.trim() : '';
  if (!key) {
    return null;
  }

  return {
    key,
    variable: getFeatureGateVariable(item.var),
  };
}

function getFeatureGateConfigFromRouteMeta(meta) {
  const raw = meta?.featureGate;
  const items = Array.isArray(raw) ? raw : [raw];
  const entries = items.map(parseFeatureGateEntry).filter(Boolean);

  if (entries.length === 0) {
    return null;
  }

  return {
    entries,
    mode: Array.isArray(raw) && meta?.featureGateMode === 'any' ? 'any' : 'all',
  };
}

function isFeatureAllowedForGate(testingStore, key, variable) {
  if (!testingStore.hasEnabledFeature(key)) {
    return false;
  }
  if (!variable) {
    return true;
  }
  return testingStore.getFeatureVariables(key)?.[variable] === true;
}

function areFeaturesEnabledForGate(testingStore, entries, mode) {
  const isAllowed = (entry) => isFeatureAllowedForGate(testingStore, entry.key, entry.variable);
  if (mode === 'any') {
    return entries.some(isAllowed);
  }
  return entries.every(isAllowed);
}

export default defineNuxtRouteMiddleware((to) => {
  const gateConfig = getFeatureGateConfigFromRouteMeta(to.meta);
  if (!gateConfig) {
    throw createError({
      statusCode: 404,
      data: { isFeatureGate: true },
    });
  }

  const testingStore = useTestingStore();
  const isAllowed = areFeaturesEnabledForGate(
    testingStore,
    gateConfig.entries,
    gateConfig.mode,
  );

  if (!isAllowed) {
    throw createError({
      statusCode: 404,
      data: { isFeatureGate: true },
    });
  }
});
